import { produce } from 'immer';

import { LayoutFeatureArea } from '@paragon/contract-library';
import { Point2DInterface } from '@paragon/geometry-library/source/scripts/Point2D';
import { enqueueError, enqueueWarning } from '@paragon/notification-tools';
import { forceArray, MappedOmit } from '@paragon/utilities';

import {
  getAllFeaturePatternContainersDictionary,
  getVisibleFeaturePatternContainerGuids,
} from '../../selectors/layoutDataSelectors';
import { applicationOnInsertingFeatureArea, isInsertionFeatureAreaFullyDefined } from '../../slices/applicationSlice';
import { layoutSlice } from '../../slices/layoutSlice';
import { ensureLevelIsVisible } from '../levelVisibilityActions';
import { selectItem } from '../selectActions';
import { AsyncThunkActionVoid, ThunkActionVoid } from '../thunkActions';
import { saveFeaturePatternContainers } from './featurePatternContainerActions';

export const startInsertingFeatureArea =
  (featurePatternContainerGuid?: string): ThunkActionVoid =>
  (dispatch, getState) => {
    const state = getState();

    if (featurePatternContainerGuid == null) {
      const featurePatternContainerGuids = getVisibleFeaturePatternContainerGuids(state);

      if (featurePatternContainerGuids.size > 0) {
        featurePatternContainerGuid = Array.from(featurePatternContainerGuids)[0];
      }
    }

    const featurePatternContainer = featurePatternContainerGuid
      ? getAllFeaturePatternContainersDictionary(state)[featurePatternContainerGuid]
      : null;

    if (featurePatternContainerGuid == null || featurePatternContainer == null) {
      dispatch(enqueueWarning('No feature pattern containers currently present.'));
      return;
    }

    if (featurePatternContainer.levelGuid) {
      dispatch(ensureLevelIsVisible(featurePatternContainer.levelGuid, { exclusive: true }));
    }

    dispatch(applicationOnInsertingFeatureArea(featurePatternContainerGuid));
  };

export const finishInsertingFeatureArea = (): AsyncThunkActionVoid => async (dispatch, getState) => {
  const { insertingObjectInfo } = getState().application;

  if (insertingObjectInfo?.type !== 'featureArea') return;
  if (!isInsertionFeatureAreaFullyDefined(insertingObjectInfo.data.value)) return;

  const area = { ...insertingObjectInfo.data.value, guid: crypto.randomUUID() };

  dispatch(
    layoutSlice.actions.featureAreasCreated({
      areas: [area],
      featurePatternContainerGuid: insertingObjectInfo.data.featurePatternContainerGuid,
    }),
  );

  dispatch(selectItem(area, 'featureArea'));

  await dispatch(saveFeaturePatternContainers());
};

export interface EditedFeatureArea {
  guid: string;
  containerGuid: string;
  editedArea: MappedOmit<LayoutFeatureArea, 'guid'>;
}

export const editFeatureAreas =
  (
    instructions: EditedFeatureArea | EditedFeatureArea[],
    options?: { createdAreas: { [containerGuid: string]: LayoutFeatureArea[] } },
  ): AsyncThunkActionVoid =>
  async (dispatch, getState) => {
    const state = getState();

    // the payload that we will eventually dispatch
    const upsertedFeatureAreas = [] as { area: LayoutFeatureArea; containerGuid: string }[];

    const existingFeatureAreas = Object.fromEntries(
      Object.values(state.layout.featurePatternContainers)
        .flatMap((container) => container.featureAreas)
        .map((area) => [area.guid, area] as const),
    );

    const existingFeatureAreaGuids = new Set(Object.keys(existingFeatureAreas));

    const createdFeatureAreas = Object.entries(options?.createdAreas ?? {}).flatMap(([containerGuid, areas]) =>
      areas.map((area) => ({ area, containerGuid })),
    );

    // first integrate the newly created feature areas
    for (const createdFeature of createdFeatureAreas) {
      if (existingFeatureAreaGuids.has(createdFeature.area.guid)) {
        dispatch(enqueueError('Unable to edit feature areas. Please contact Paragon Customer Support.'));
        throw new Error(`Feature area with guid ${createdFeature.area.guid} already exists.`);
      }

      existingFeatureAreaGuids.add(createdFeature.area.guid);
      existingFeatureAreas[createdFeature.area.guid] = createdFeature.area;

      // ensure it is marked for upsertion in case it does not have an edit instruction
      upsertedFeatureAreas.push(createdFeature);
    }

    // then process the edit instructions
    for (const editInstruction of forceArray(instructions)) {
      const existingFeatureArea = existingFeatureAreas[editInstruction.guid];
      if (existingFeatureArea == null) {
        dispatch(enqueueError('Unable to edit feature areas. Please contact Paragon Customer Support.'));
        throw new Error(`Unable to find feature area with guid ${editInstruction.guid} to edit.`);
      }

      upsertedFeatureAreas.push({
        containerGuid: editInstruction.containerGuid,
        area: { ...editInstruction.editedArea, guid: editInstruction.guid },
      });
    }

    dispatch(layoutSlice.actions.featureAreasUpserted(upsertedFeatureAreas));

    await dispatch(saveFeaturePatternContainers());
  };

export const setFeatureAreaPosition =
  (guid: string, point: Point2DInterface): AsyncThunkActionVoid =>
  async (dispatch, getState) => {
    const state = getState();
    const featurePatternContainers = Object.values(state.layout.featurePatternContainers);

    for (const container of featurePatternContainers) {
      const featureArea = container.featureAreas.find((featureArea) => featureArea.guid === guid);

      if (featureArea) {
        dispatch(
          editFeatureAreas({
            guid,
            containerGuid: container.guid,
            editedArea: produce(featureArea, (draft) => {
              draft.area.position = point;
            }),
          }),
        );

        return;
      }
    }

    throw new Error('Feature area not found to edit.');
  };
